import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, Layers, ChevronDown, Check } from "lucide-react";

const shortAddr = (a) => {
  const s = String(a || "");
  return s.toLowerCase().startsWith("0x") && s.length > 12 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
};

/**
 * Header wallet-context switcher.
 * - Admins (incl. super admin): "All wallets" default + every connected
 *   revenue-source wallet / tree root — picking one scopes the whole app
 *   (org chart, dashboard, revenue, wallets) to that wallet + its downline.
 * - Members/leaders: their linked tiles (unchanged behavior).
 * Hidden for demo and for users with nothing to switch between.
 */
export default function WalletSwitcher() {
  const { user, activeWalletId, setActiveWalletId } = useAuth();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [allLabel, setAllLabel] = useState("All wallets");
  const [isAdminList, setIsAdminList] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!user || user.is_demo) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/my-wallets");
        if (!alive) return;
        setWallets(data?.wallets || []);
        setIsAdminList(!!data?.admin);
        if (data?.all_label) setAllLabel(data.all_label);
      } catch { /* silent — switcher just stays hidden */ }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    const onClick = (e) => { if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user || user.is_demo) return null;
  const isAdmin = user.role === "admin";
  if (isAdmin && wallets.length < 1) return null;
  if (!isAdmin && wallets.length < 2) return null;

  const active = wallets.find((w) => w.id === activeWalletId) || null;
  const scoped = isAdmin ? !!active : true;
  const label = active ? active.name : (isAdmin ? allLabel : wallets[0]?.name);

  const pick = (id) => {
    setActiveWalletId(id);
    setOpen(false);
    // Scope is enforced backend-side via the X-Active-Wallet header — reload so
    // every panel refetches with the newly selected wallet.
    window.location.reload();
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="wallet-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border hover:bg-black/[0.03] transition-colors text-xs font-medium"
        style={{ borderColor: isAdmin && scoped ? "rgba(64,96,255,0.45)" : "rgba(0,0,0,0.1)", background: isAdmin && scoped ? "rgba(64,96,255,0.06)" : undefined }}
        data-testid="wallet-switcher-button"
        title="Switch the wallet you're viewing"
      >
        {isAdmin && !scoped ? <Layers size={13} style={{ color: "#4060FF" }} /> : <Wallet size={13} style={{ color: "#4060FF" }} />}
        <span className="max-w-[160px] truncate">{label}</span>
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 glass shadow-lg z-50 min-w-[260px] py-2 max-h-[min(70vh,520px)] overflow-y-auto overscroll-contain"
          style={{ borderRadius: 14, background: "#FFFFFF" }}
          data-testid="wallet-switcher-menu"
        >
          {isAdminList && (
            <>
              <button
                type="button"
                onClick={() => pick("")}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-black/[0.03] transition-colors"
                data-testid="wallet-switcher-all"
              >
                <Layers size={14} style={{ color: "#4060FF" }} />
                <span className="flex-1 truncate font-medium">{allLabel}</span>
                {!active && <Check size={14} style={{ color: "#00863A" }} />}
              </button>
              <div className="my-1.5 border-t border-black/5" />
            </>
          )}
          {wallets.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => pick(w.id)}
              className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-black/[0.03] transition-colors"
              data-testid={`wallet-switcher-item-${w.id}`}
            >
              <Wallet size={14} style={{ color: w.wallet_kind === "Staking" ? "#C2185B" : w.wallet_kind === "Both" ? "#7C3AED" : "#4060FF" }} />
              <span className="flex-1 min-w-0">
                <span className="block truncate">{w.name}{w.is_root ? " · root" : ""}</span>
                {w.wallet_name && (
                  <span className="block text-[10px] text-ink-3 font-mono truncate">{shortAddr(w.wallet_name)}</span>
                )}
              </span>
              {active?.id === w.id && <Check size={14} style={{ color: "#00863A" }} />}
            </button>
          ))}
          {!isAdmin && (
            <>
              <div className="my-1.5 border-t border-black/5" />
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/select-wallet"); }}
                className="w-full px-3 py-1.5 text-left text-xs text-ink-3 hover:bg-black/[0.03] transition-colors"
                data-testid="wallet-switcher-manage"
              >
                View picker page →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
